// PHA-1225: the Program Confirmation Form on the patient chart, under (and separate from) the
// verbal Consent Status. One line per form sent: when it was sent and by whom, and the answer
// with its date — green for Yes, red for No (Zack, 2026-10-28). Each form opens as a PDF of
// the text the patient was shown and their answer, and an answer can be filed into the EHR.
// A "No" (or a failed send) is called out so the care team switches channel or sends again.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { ConsentRequest, patientApi } from '../services/api';
import { CONSENT_FORM_TITLE } from '../utils/consentText';

interface Props {
  patientId: string;
  /** Bumped by the chart after a form is sent, to reload the list. */
  refreshKey?: number;
}

const when = (iso: string | null) => (iso ? format(new Date(iso), 'MMM d, yyyy h:mm a') : '');
const day = (iso: string | null) => (iso ? format(new Date(iso), 'MMM d, yyyy') : '');

const LANGUAGE_NAMES: Record<string, string> = {
  'es-US': 'Spanish',
  'fr-FR': 'French',
  'he-IL': 'Hebrew',
  'ar-001': 'Arabic',
};

const PUSH_LABELS: Record<string, string> = {
  pending: 'Sending to EHR…',
  accepted: 'Sent to EHR',
  filed: 'Filed in EHR',
  failed: 'EHR upload failed',
};

export function describeConsentRequest(r: ConsentRequest): { label: string; color: 'success' | 'error' | 'warning' | 'default' } {
  if (r.send_status === 'failed') return { label: 'Text not sent', color: 'error' };
  if (r.response === 'yes') return { label: `Yes · ${day(r.responded_at)}`, color: 'success' };
  if (r.response === 'no') return { label: `No · ${day(r.responded_at)}`, color: 'error' };
  if (r.expired) return { label: 'No answer, link expired', color: 'warning' };
  return { label: 'Waiting for answer', color: 'default' };
}

/** Decode the PDF the backend returns as base64 into a Blob. */
export function consentPdfBlob(pdfBase64: string): Blob {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}

const ConsentRequestsPanel: React.FC<Props> = ({ patientId, refreshKey = 0 }) => {
  const [requests, setRequests] = useState<ConsentRequest[]>([]);
  const [ehrPushEnabled, setEhrPushEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushingId, setPushingId] = useState<string | null>(null);
  const [pdfId, setPdfId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await patientApi.listConsentRequests(patientId);
      setRequests(data.requests ?? []);
      setEhrPushEnabled(data.ehr_push_enabled === true);
    } catch (err) {
      // Advisory section: a failed lookup must never block the chart.
      console.error('PHA-1225: failed to load Program Confirmation Forms:', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const openPdf = async (r: ConsentRequest) => {
    // Open the tab now, inside the click: a window opened after the request returns is
    // blocked as a popup. It is pointed at the PDF once it arrives.
    const tab = window.open('', '_blank');
    setPdfId(r.id);
    try {
      const { filename, pdf_base64: pdfBase64 } = await patientApi.getConsentPdf(r.id);
      const url = URL.createObjectURL(consentPdfBlob(pdfBase64));
      if (tab) {
        tab.location.href = url;
      } else {
        // Popups blocked entirely: download it instead.
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `${CONSENT_FORM_TITLE}.pdf`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      tab?.close();
      toast.error(err?.message || 'Failed to open the PDF');
    } finally {
      setPdfId(null);
    }
  };

  const pushToEhr = async (r: ConsentRequest, force = false) => {
    setPushingId(r.id);
    try {
      await patientApi.pushConsentToEhr(r.id, force);
      toast.success(`${CONSENT_FORM_TITLE} sent to the EHR.`);
      await load();
    } catch (err: any) {
      if (err?.alreadySent && !force) {
        if (window.confirm(`${err.message}\n\nSend another copy to the EHR?`)) {
          await pushToEhr(r, true);
          return;
        }
      } else {
        toast.error(err?.message || 'Failed to send the form to the EHR');
      }
    } finally {
      setPushingId(null);
    }
  };

  if (!loading && requests.length === 0) return null;
  const latest = requests[0];

  return (
    <Box sx={{ mt: 2 }} data-testid="consent-requests-panel">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {CONSENT_FORM_TITLE} {loading && <CircularProgress size={12} sx={{ ml: 1 }} />}
      </Typography>
      {latest?.response === 'no' && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The patient answered No on {when(latest.responded_at)}. Call the patient or use another
          way to reach them, or send the form again if this was a mistake.
        </Alert>
      )}
      {latest?.send_status === 'failed' && (
        <Alert severity="error" sx={{ mb: 1 }}>
          The last form was not sent{latest.send_error ? `: ${latest.send_error}` : '.'} Use
          another way to reach the patient, or fix the phone number and send again.
        </Alert>
      )}
      <Stack spacing={1}>
        {requests.map((r) => {
          const status = describeConsentRequest(r);
          const languageName = r.language && r.language !== 'en-US' ? LANGUAGE_NAMES[r.language] ?? r.language : null;
          return (
            <Box
              key={r.id}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
                    p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
              data-testid="consent-request-row"
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
                Sent {day(r.sent_at)}
                {r.sent_by_name ? ` by ${r.sent_by_name}` : ''}
                {languageName ? ` · ${languageName}` : ''}
                {!r.response && !r.expired && r.send_status === 'sent' && r.expires_at &&
                  ` · link open until ${day(r.expires_at)}`}
              </Typography>
              <Chip
                size="small"
                label={status.label}
                color={status.color}
                variant={r.response ? 'filled' : 'outlined'}
                title={r.responded_at ? `Answered ${when(r.responded_at)}` : undefined}
                data-testid="consent-request-status"
              />
              {r.ehr_push_status && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={PUSH_LABELS[r.ehr_push_status] ?? r.ehr_push_status}
                  color={r.ehr_push_status === 'failed' ? 'error' : 'default'}
                  title={r.ehr_push_error ?? undefined}
                />
              )}
              {r.send_status !== 'failed' && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={pdfId === r.id ? <CircularProgress size={12} /> : <PictureAsPdf fontSize="small" />}
                  disabled={pdfId === r.id}
                  onClick={() => openPdf(r)}
                  data-testid="consent-open-pdf"
                >
                  PDF
                </Button>
              )}
              {ehrPushEnabled && r.response && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={pushingId === r.id}
                  onClick={() => pushToEhr(r)}
                  data-testid="consent-push-to-ehr"
                >
                  {pushingId === r.id
                    ? 'Sending…'
                    : r.ehr_push_status && r.ehr_push_status !== 'failed' ? 'Send to EHR again' : 'Send to EHR'}
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ConsentRequestsPanel;
